import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../atoms/icon-inline/nn-icon-inline.js';
/**
 * Connection molecule.
 * A component with disconnected and connected html.
 * Uses icon-inline atom in disconnected mode.
 * When not connected, only button with icon-inline atom inside, URL used is customerUrl.
 * When connected, shows user and user ID in a button, URL used is customerUrl.
 * When connected, an additional logout button is shown, URL used is logoutUrl .
 *
 * connected - The Connected boolean variable. If set at true, connected block is shown.
 * customerUrl - The customer website URL. Url used in connected mode only.
 * user - The user login. Shown in connected mode only.
 * userId - The user ID. Shown in connected mode only.
 * logoutUrl - The logout URL. Shown in connected mode only.
 * --nn-icon-inline_icon-inline_color - overrides color of icon-inline text.
 * --nn-icon-inline_icon-inline_font-size - overrides font-size of icon-inline text.
 */
@customElement('nn-connection')
export class Connection extends LitElement {
  static styles = css`
    :host {
      --nn-icon-inline_icon-inline_color: #fff;
      --nn-icon-inline_icon-inline_font-size: 13px;
    }
    .custom-flex {
      align-items: center;
      display: flex;
      justify-content: flex-start;
    }
    .anchor {
      align-items: center;
      background: var(--nn-connection_anchor_background, transparent);
      border: 1px solid var(--nn-connection_anchor_border-color, #f8f8f8);
      border-radius: var(--nn-connection_anchor_border-radius, 32px);
      color: var(--nn-connection_anchor_color, #f8f8f8);
      display: flex;
      font-size: var(--nn-connection_anchor_font-size, 0.813em);
      height: var(--nn-connection_anchor_height, 36px);
      padding: var(--nn-connection_anchor_padding, 0 18px);
      text-decoration: none;
      transition: var(--nn-connection_anchor_transition, 0.3s);
    }
    /* -----------CONNECTED MODE-------------- */
    .user-connected-mode {
      align-items: center;
      display: inline-flex;
    }
    .user-connected--cta {
      border: 1px solid
        var(--nn-connection_user-connected--cta_border-color, #f8f8f8);
      border-radius: var(
        --nn-connection_user-connected--cta_border-radius,
        18px
      );
      font-size: var(--nn-connection_user-connected--cta_font-size, 13px);
      height: var(--nn-connection_user-connected--cta_height, 36px);
      line-height: 1;
      margin-left: var(--nn-connection_user-connected--cta_margin-left, 10px);
      padding: var(--nn-connection_user-connected--cta_padding, 0 18px);
    }
    .custom-flex--img {
      margin-right: var(--nn-connection_custom-flex--img_margin-right, 5px);
    }
    .user-connected-mode .custom-flex--text {
      text-align: left;
    }
    .user-connected-mode .user-name {
      display: block;
      line-height: 1.35;
      max-width: var(
        --nn-connection_user-connected-mode_user-name_max-width,
        110px
      );
      overflow: hidden;
      text-align: left;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .user-connected-mode .user-id {
      font-family: 'font-light';
      font-size: var(
        --nn-connection_user-connected-mode_user-id_font-size,
        11px
      );
      line-height: 1.35;
    }
    .connected-mode .mobile-user--id {
      color: var(--nn-connection_connected-mode_mobile-user--id_color, #d93939);
      font-family: 'font-heavy';
    }
    .connected-mode .mobile-user--link {
      color: var(--nn-connection_connected-mode_mobile-user--link_color, #000);
      display: block;
      font-family: 'font-heavy';
      margin: var(
        --nn-connection_connected-mode_mobile-user--link_margin,
        30px 0
      );
      text-decoration: none;
      width: 100%;
    }
    .connected-mode .mobile-user--logout {
      text-align: center;
    }
    .connected-mode .mobile-cta,
    .connected-mode .mobile-cta:hover,
    .connected-mode .mobile-cta:active,
    .connected-mode .mobile-cta:focus,
    .connected-mode .mobile-cta:visited {
      align-items: center;
      background: var(
        --nn-connection_connected-mode_mobile-cta_background,
        #fff
      );
      border: 1px solid
        var(--nn-connection_connected-mode_mobile-cta_border-color, #7c7c7c);
      border-radius: var(
        --nn-connection_connected-mode_mobile-cta_border-radius,
        10px
      );
      color: var(--nn-connection_connected-mode_mobile-cta_color, #595959);
      display: inline-flex;
      font-family: 'font-medium';
      font-size: var(--nn-connection_connected-mode_mobile-cta_font-size, 1em);
      height: var(--nn-connection_connected-mode_mobile-cta_height, 41px);
      justify-content: center;
      padding: var(--nn-connection_connected-mode_mobile-cta_padding, 0 15px);
      text-decoration: none;
      width: var(--nn-connection_connected-mode_mobile-cta_width, 170px);
    }
    .logout {
      align-items: center;
      color: var(--nn-connection_logout_color, #f8f8f8);
      display: flex;
      padding: var(--nn-connection_logout_padding, 5px 10px);
      transition: var(--nn-connection_logout_transition, 0.3s);
    }
    @media (min-width: 767px) {
      .anchor {
        margin-left: var(--nn-connection_anchor_margin-left, 10px);
      }
    }
    .user-connected--cta.anchor {
      --nn-icon-inline_icon-inline_font-size: 1em;
    }
    @media (max-width: 600px) {
      [slot='text'] {
        display: var(--nn-connection_slot-text_display, inline-block);
      }
    }
  `;

  @property({ type: Boolean }) connected = false;

  @property({ type: String }) customerUrl = 'https://client.nordnet.com';

  @property({ type: String }) user = '';

  @property({ type: String }) userId = '';

  @property({ type: String }) logoutUrl = 'https://nordnet.com/logout';

  @property({ type: Boolean }) useLogoutAction = false;

  private dispatchLogout(e: Event) {
    if (this.useLogoutAction) {
      e.preventDefault();
      this.dispatchEvent(new CustomEvent('logout'));
    }
  }

  render() {
    if (!this.connected) {
      return html`<a
        id="header-customer-space-link"
        class="anchor"
        href="${this.customerUrl}"
        title="Mon compte"
      >
        <nn-icon-inline>
          <svg
            slot="icon"
            class="img-responsive"
            width="16"
            height="17"
            viewBox="0 0 16 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>Espace client</title>
            <path
              d="M8.19337 0.334351C5.93876 0.334351 4.10449 2.22319 4.10449 4.54488C4.10449 6.86656 5.93876 8.7554 8.19337 8.7554C10.448 8.7554 12.2823 6.86656 12.2823 4.54488C12.2823 2.22319 10.448 0.334351 8.19337 0.334351ZM8.19337 7.07119C6.84077 7.07119 5.74004 5.93772 5.74004 4.54488C5.74004 3.15203 6.84077 2.01856 8.19337 2.01856C9.54598 2.01856 10.6467 3.15203 10.6467 4.54488C10.6467 5.93772 9.54598 7.07119 8.19337 7.07119ZM14.7356 16.3344C15.1872 16.3344 15.5534 15.9682 15.5534 15.5166V15.4922C15.5534 12.2426 12.9847 9.59751 9.82893 9.59751H6.55782C3.4012 9.59751 0.833374 12.2426 0.833374 15.4922V15.5166C0.833374 15.9682 1.19951 16.3344 1.65115 16.3344C2.1028 16.3344 2.46893 15.9682 2.46893 15.5166V15.4922C2.46893 13.1706 4.30321 11.2817 6.55782 11.2817H9.82893C12.0835 11.2817 13.9178 13.1706 13.9178 15.4922V15.5166C13.9178 15.9682 14.2839 16.3344 14.7356 16.3344Z"
              fill="white"
            ></path>
          </svg>
          <span slot="text">Mon compte</span>
        </nn-icon-inline>
      </a>`;
    }
    return html` <div class="user-connected-mode">
      <nn-link
        textLink="Mon Compte"
        idlink="prenav-customer-connected-link"
        hrefLink="${this.customerUrl}"
        class="user-connected--cta anchor"
      >
        <svg
          slot="icon"
          class="img-responsive"
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <title>Mon Compte</title>
          <path
            d="M8.19337 0.334351C5.93876 0.334351 4.10449 2.22319 4.10449 4.54488C4.10449 6.86656 5.93876 8.7554 8.19337 8.7554C10.448 8.7554 12.2823 6.86656 12.2823 4.54488C12.2823 2.22319 10.448 0.334351 8.19337 0.334351ZM8.19337 7.07119C6.84077 7.07119 5.74004 5.93772 5.74004 4.54488C5.74004 3.15203 6.84077 2.01856 8.19337 2.01856C9.54598 2.01856 10.6467 3.15203 10.6467 4.54488C10.6467 5.93772 9.54598 7.07119 8.19337 7.07119ZM14.7356 16.3344C15.1872 16.3344 15.5534 15.9682 15.5534 15.5166V15.4922C15.5534 12.2426 12.9847 9.59751 9.82893 9.59751H6.55782C3.4012 9.59751 0.833374 12.2426 0.833374 15.4922V15.5166C0.833374 15.9682 1.19951 16.3344 1.65115 16.3344C2.1028 16.3344 2.46893 15.9682 2.46893 15.5166V15.4922C2.46893 13.1706 4.30321 11.2817 6.55782 11.2817H9.82893C12.0835 11.2817 13.9178 13.1706 13.9178 15.4922V15.5166C13.9178 15.9682 14.2839 16.3344 14.7356 16.3344Z"
            fill="#fff"
          />
        </svg>
        <span class="custom-flex--text" slot="text">
          <span class="user-name">${this.user}</span>
          <span class="user-id">Client n° ${this.userId}</span>
        </span>
      </nn-link>
      <a
        class="logout"
        @click="${this.dispatchLogout}"
        id="megamenu-logout-link-logout"
        href="${this.logoutUrl}"
        title="Se déconnecter"
      >
        <svg
          width="21"
          height="22"
          viewBox="0 0 21 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>Se déconnecter</title>
          <path
            d="M10.8334 1.75098V9.77181"
            stroke="white"
            stroke-width="2"
            stroke-miterlimit="10"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M7.1625 3.79688C4.04583 5.20104 1.875 8.32187 1.875 11.9594C1.875 16.9094 5.8875 20.9177 10.8333 20.9177C15.7792 20.9177 19.7917 16.9094 19.7917 11.9594C19.7917 8.32187 17.6208 5.19687 14.5083 3.79688"
            stroke="white"
            stroke-width="2"
            stroke-miterlimit="10"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </a>
    </div>`;
  }
}
